// modules/WorldInteraction/WorldInteractionHandler.js
// 世界场景交互处理模块 - 处理NPC交互、商店、旅馆等

import DebugHelper from '../DebugHelper.js';
import SaveSystem from '../SaveSystem.js';
import MessageHelper from '../MessageHelper.js';
import ItemDataLoader from '../ItemDataLoader.js';
import GameStateStore from '../GameStateStore.js';

export default class WorldInteractionHandler {
    constructor(scene) {
        this.scene = scene;
    }
    
    /**
     * 与NPC交互
     * @param {Object} npc - NPC对象
     */
    interactWithNPC(npc) {
        if (!npc || !this.scene.worldNPC) return;
        
        // 让NPC转向面向玩家（必须在交互开始前执行）
        if (npc.sprite && this.scene.playerSprite) {
            const playerX = this.scene.playerSprite.x;
            const playerY = this.scene.playerSprite.y;
            DebugHelper.log('开始让NPC转向面向玩家');
            this.scene.worldNPC.turnNPCTowardsPlayer(npc.sprite, playerX, playerY);
        }
        
        const npcName = npc.data?.name || 'NPC';
        const npcData = npc.data;
        const npcConfig = npcData.config || {}; // 获取NPC配置（整个配置对象）
        
        // 检查NPC是否有保存功能（save字段在配置顶层，类似buy字段）
        if (npcConfig.save === true) {
            DebugHelper.log(`${npcName} 有保存功能`);
            this.openSaveMenu(npcName);
            return;
        }
        
        // 检查NPC是否有商店功能（buy字段在配置顶层）
        if (npcConfig.buy && Array.isArray(npcConfig.buy) && npcConfig.buy.length > 0) {
            DebugHelper.log(`${npcName} 有商店功能，商品数量: ${npcConfig.buy.length}`);
            
            // 加载商品数据（支持两种方式：商品ID数组或商品对象数组）
            const shopItems = this.loadShopItems(npcConfig.buy);
            
            if (shopItems.length === 0) {
                DebugHelper.warn(`${npcName} 的商品列表为空，无法打开商店`);
                this.scene.worldUI.showMessage(`${npcName} 的商品暂时缺货...`);
                return;
            }
            
            // 询问是否要购物
            const hasSell = npcConfig.sell === true;
            const dialogContent = hasSell 
                ? ['你好，我这里有些不错的商品。', '可以购买，也可以出售物品。', '想要看看吗？']
                : ['你好，我这里有些不错的商品。', '想要看看吗？'];
            
            this.scene.worldUI.showDialog({
                title: npcName,
                content: dialogContent,
                callback: () => {
                    this.openShop(shopItems, npcName, hasSell);
                }
            });
            return;
        }
        
        // 检查NPC是否有旅馆功能（sleep字段在配置顶层）
        if (npcConfig.sleep === true) {
            DebugHelper.log(`${npcName} 有旅馆功能`);
            this.openHotelMenu(npcName, npcConfig);
            return;
        }
        
        // 检查NPC是否有战车中心特殊功能
        if (npcConfig.vehicleCenter) {
            const functionType = npcConfig.vehicleCenter;
            DebugHelper.log(`${npcName} 有战车中心功能: ${functionType}`);
            
            if (functionType === 'equipment') {
                // 装备管理
                this.openVehicleEquipmentMenu('equip', npcName);
            } else if (functionType === 'craft') {
                // 碎片合成
                this.openVehicleEquipmentMenu('craft', npcName);
            } else if (functionType === 'lottery') {
                // 抽奖和卖图纸
                this.openBlueprintAndLotteryMenu(npcName);
            } else if (functionType === 'material') {
                // 材料合成/分解
                this.openVehicleEquipmentMenu('disassemble', npcName);
            } else if (functionType === 'supply') {
                // 补给（弹药、图层、SP）
                this.openSupplyMenu(npcName);
            }
            return;
        }
        
        // 原有的对话功能
        const dialog = this.scene.worldNPC.getNPCDialog(npc);
        
        if (dialog) {
            DebugHelper.log(`与 ${npcName} 对话: ${dialog}`);
            this.scene.worldUI.showDialog(npcName, [dialog]);
        } else {
            DebugHelper.log(`${npcName} 无对话内容`);
            this.scene.worldUI.showMessage(`${npcName} 保持沉默...`);
        }
    }
    
    /**
     * 打开战车装备管理菜单
     * @param {string} mode - 模式 (equip/craft/disassemble)
     * @param {string} npcName - NPC名称
     */
    openVehicleEquipmentMenu(mode, npcName) {
        const currentSceneKey = this.scene.scene.key;
        this.scene.scene.pause(currentSceneKey);
        this.scene.scene.launch('VehicleEquipmentScene', {
            mode: mode,
            previousSceneKey: currentSceneKey
        });
    }
    
    /**
     * 打开图纸和抽奖菜单
     * @param {string} npcName - NPC名称
     */
    openBlueprintAndLotteryMenu(npcName) {
        const player = GameStateStore.getPlayer();
        if (!player) {
            MessageHelper.showError(this.scene, '玩家数据无效');
            return;
        }
        
        // 直接打开图纸和抽奖场景（类似商店）
        const currentSceneKey = this.scene.scene.key;
        this.scene.scene.pause(currentSceneKey);
        this.scene.scene.launch('BlueprintLotteryScene', {
            npcName: npcName,
            previousSceneKey: currentSceneKey
        });
    }
    
    /**
     * 打开补给菜单
     * @param {string} npcName - NPC名称
     */
    openSupplyMenu(npcName) {
        const player = GameStateStore.getPlayer();
        if (!player || !player.vehicle) {
            MessageHelper.showError(this.scene, '没有战车');
            return;
        }
        
        const vehicle = player.vehicle;
        const supplyItems = [
            { id: 'cannon_ammo', name: '主炮弹药', price: 50, type: 'ammo', ammoType: 'cannon_ammo', amount: 10 },
            { id: 'se_ammo', name: 'S-E弹药', price: 100, type: 'ammo', ammoType: 'se_ammo', amount: 5 },
            { id: 'corrosion_layer', name: '抗腐蚀图层', price: 200, type: 'layer', amount: 50 },
            { id: 'sp_restore', name: 'SP恢复', price: 150, type: 'sp', amount: 100 }
        ];
        
        // 打开商店（使用补给商品）
        this.openShop(supplyItems, npcName, false);
    }
    
    /**
     * 打开保存菜单
     * @param {string} npcName - NPC名称
     */
    openSaveMenu(npcName) {
        const scene = this.scene;
        
        // 显示对话（模仿商店模式）
        scene.worldUI.showDialog({
            title: npcName,
            content: ['你好，我可以帮你保存游戏进度。', '要保存游戏吗？'],
            callback: () => {
                // 对话结束后，延迟一下再显示确认对话框（确保对话框UI完全关闭）
                scene.time.delayedCall(200, () => {
                    DebugHelper.log('显示保存确认对话框');
                    MessageHelper.showConfirm(
                        scene,
                        '确认要保存游戏吗？',
                        () => {
                            // 确认保存
                            DebugHelper.log('用户确认保存');
                            
                            // 保存当前场景信息
                            const currentScene = scene.scene.key;
                            if (currentScene) {
                                GameStateStore.setCurrentScene(currentScene);
                            }
                            
                            // 保存游戏到服务器数据库
                            SaveSystem.save(0).then(success => {
                                if (success) {
                                    MessageHelper.showSuccess(scene, '游戏已保存到服务器！');
                                } else {
                                    MessageHelper.showError(scene, '保存失败，请重试。');
                                }
                            }).catch(error => {
                                DebugHelper.error('保存游戏失败:', error);
                                MessageHelper.showError(scene, '保存失败，请重试。');
                            });
                        },
                        () => {
                            // 取消保存
                            DebugHelper.log('用户取消了保存');
                        }
                    );
                });
            }
        });
    }

    /**
     * 打开旅馆菜单
     * @param {string} npcName - NPC名称
     * @param {Object} npcConfig - NPC配置
     */
    openHotelMenu(npcName, npcConfig) {
        const scene = this.scene;
        const price = npcConfig.price || 50;
        
        // 检查玩家是否有足够的钱
        const playerMoney = GameStateStore.getPlayer()?.money || 0;
        
        // 显示对话
        const dialogContent = playerMoney >= price
            ? ['欢迎来到旅馆！', `住宿费用是 ${price} G。`, '要在这里休息吗？']
            : ['欢迎来到旅馆！', `住宿费用是 ${price} G。`, '但是你的钱不够...'];
        
        scene.worldUI.showDialog({
            title: npcName,
            content: dialogContent,
            callback: () => {
                // 如果钱不够，直接返回
                if (playerMoney < price) {
                    MessageHelper.showError(scene, `钱不够！需要 ${price} G，但你只有 ${playerMoney} G。`);
                    return;
                }
                
                // 对话结束后，延迟一下再显示确认对话框
                scene.time.delayedCall(200, () => {
                    DebugHelper.log('显示旅馆确认对话框');
                    MessageHelper.showConfirm(
                        scene,
                        `确认要花费 ${price} G 在这里休息吗？\n（将恢复全部HP）`,
                        () => {
                            // 确认睡觉
                            DebugHelper.log('用户确认睡觉');
                            
                            // 扣除费用
                            const hotelPlayer = GameStateStore.getPlayer();
                            if (hotelPlayer) {
                                const maxHp = hotelPlayer.maxHp || 50;
                                GameStateStore.patchPlayer({
                                    money: hotelPlayer.money - price,
                                    hp: maxHp
                                });
                                
                                DebugHelper.log(`已扣除 ${price} G，恢复HP到 ${maxHp}`);
                                
                                // 更新UI显示
                                if (scene.worldUI) {
                                    scene.worldUI.updateHUD(GameStateStore.getPlayer());
                                }
                                
                                // 播放BGM（如果配置了）
                                if (npcConfig.bgm && scene.sound) {
                                    // 停止当前BGM
                                    if (scene.bgm) {
                                        scene.bgm.stop();
                                    }
                                    // 播放睡觉BGM
                                    try {
                                        scene.bgm = scene.sound.add(npcConfig.bgm, { volume: 0.1});
                                        scene.bgm.play();
                                    } catch (error) {
                                        DebugHelper.warn(`无法播放BGM: ${npcConfig.bgm}`, error);
                                    }
                                }
                                
                                MessageHelper.showSuccess(scene, `休息完毕！HP已恢复！`);
                            }
                        },
                        () => {
                            // 取消睡觉
                            DebugHelper.log('用户取消了睡觉');
                        }
                    );
                });
            }
        });
    }

    /**
     * 加载商店商品数据
     * 支持两种方式：
     * 1. 商品ID数组（新方式）：["pistol", "rifle", "armor"] - 从items.json加载
     * 2. 商品对象数组（旧方式，向后兼容）：[{id: "pistol", name: "手枪", ...}, ...]
     * @param {Array<string|Object>} buyConfig - 商品配置（ID数组或对象数组）
     * @returns {Array<Object>} 商品数据数组
     */
    loadShopItems(buyConfig) {
        if (!Array.isArray(buyConfig) || buyConfig.length === 0) {
            return [];
        }
        
        // 检查第一个元素是否为字符串（商品ID）
        const isIdArray = typeof buyConfig[0] === 'string';
        
        if (isIdArray) {
            // 新方式：商品ID数组，从ItemDataLoader加载
            if (!ItemDataLoader.isInitialized()) {
                DebugHelper.warn('ItemDataLoader未初始化，尝试初始化...');
                ItemDataLoader.initialize(this.scene);
            }
            
            if (!ItemDataLoader.isInitialized()) {
                DebugHelper.error('ItemDataLoader初始化失败，无法加载商品数据');
                return [];
            }
            
            const items = ItemDataLoader.getItemsByIds(buyConfig);
            DebugHelper.log(`从items.json加载了 ${items.length}/${buyConfig.length} 个商品`);
            return items;
        } else {
            // 旧方式：商品对象数组，直接返回（向后兼容）
            DebugHelper.log(`使用旧方式（商品对象数组），直接返回 ${buyConfig.length} 个商品`);
            return buyConfig;
        }
    }
    
    /**
     * 打开商店
     * @param {Array} shopItems - 商品列表
     * @param {string} npcName - NPC名称
     * @param {boolean} canSell - 是否可出售
     */
    openShop(shopItems, npcName, canSell = false) {
        DebugHelper.log('打开商店:', npcName, '商品数:', shopItems.length, '可出售:', canSell);
        
        // 获取当前场景的 key（可能是 WorldScene、map_rado、map_hotel_1 等）
        const currentSceneKey = this.scene.scene.key;
        
        // 暂停当前场景
        this.scene.scene.pause(currentSceneKey);
        
        // 启动商店场景，并传递当前场景的 key，以便关闭时恢复
        this.scene.scene.launch('ShopScene', {
            shopData: shopItems,
            npcName: npcName,
            canSell: canSell,
            previousSceneKey: currentSceneKey
        });
    }
}

